/**
 * AiToolsDraftDetail — drafts-inbox click-through detail page (Phase X.3.4.7).
 *
 * Route: /t/:tenantSlug/ai-tools/drafts/:id
 *
 * View a saved draft, edit the body in place (draft status only), or
 * hard-delete it (X.3.4.8 Archive -> Delete refactor). X.3.4.9 removed
 * the redundant "Mark as finalized" action (operator direction); any
 * pre-existing finalized drafts still render read-only with the status
 * chip + can-no-longer-be-edited note. Capability-gated identically to
 * AiToolsDrafts.
 *
 * Branches on draft.tool_name. An 'assessment' report still in draft (and
 * a 'med_list') reopens in its tool; a finalized assessment report and the
 * Assessment Letter render read-only here with a "Download .docx" link;
 * 'email' (default) keeps the editable email view below.
 *
 * Data shapes (re-anchored against the real persistence model):
 *   - body              <- draft.output_text (editable textarea)
 *   - recipient / topic <- draft.input_payload.form_input (read-only)
 *   - annotations       <- draft.input_payload.annotations (read-only)
 *   - billing           <- draft.input_payload.billing_suggestion (read-only)
 * Annotations + billing are grounded in the ORIGINAL generation, not the
 * edits, so they carry a "(from initial draft)" subtitle.
 *
 * EmailDraftOutput is NOT reused here: it renders the body read-only in a
 * <pre>. This page needs an editable textarea, so it composes the
 * reusable EmailAnnotationsBlock + EmailBillingBlock directly and owns the
 * body field itself.
 *
 * Mutations invalidate both the list key (['ai-tools','drafts',slug]) and
 * the detail key (['ai-tools','draft',slug,id]) rather than setQueryData,
 * because the PATCH response shape differs from the list-summary shape.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { canUseAiTools } from '@/lib/capabilities';
import { aiToolsApi, ApiError, type UpdateDraftInput, type UpdateDraftResponse } from '@/lib/api';
import { useAiDraft } from '@/lib/aiDrafts';
import { practiceDayOf } from '@/lib/practiceDate';
import { aiRefineKeyOf, contactSentence, errorText, pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { readAiInputMaxChars } from '@/lib/aiInputLimits';
import { aiToolFailureCopy, aiToolFailureOf, type AiToolFailure } from '@/lib/aiTextTooLong';
import { useInvalidateOnCeilingRefusal } from '@/hooks/useInvalidateOnCeilingRefusal';
import { DownloadDocxButton } from '@/components/DownloadDocxButton';
import { DocumentPrintPdfButton } from '@/components/DocumentPrintPdfButton';
import { SaveToClientDocumentsButton } from '@/components/SaveToClientDocumentsButton';
import { AiToolsHeader } from '@/components/aiTools/AiToolsHeader';
import { EmptyAiToolsTile } from '@/components/aiTools/EmptyAiToolsTile';
import { PageShell } from '@/components/primitives/PageShell';
import { EmailAnnotationsBlock, visibleAnnotations } from '@/components/aiTools/EmailAnnotationsBlock';
import { EmailBillingBlock } from '@/components/aiTools/EmailBillingBlock';
import { RefinePanel } from '@/components/aiTools/RefinePanel';
import { LogAsCaseNoteAction } from '@/components/aiTools/LogAsCaseNoteAction';
import { EmailActionRows } from '@/components/aiTools/EmailActionRows';
import { HelperActionRows } from '@/components/aiTools/HelperActionRows';
import { Button } from '@/components/primitives/Button';
import { SpicePhotoReminder } from '@/components/aiTools/SpicePhotoReminder';
import { SpiceMedicalFlag } from '@/components/aiTools/SpiceMedicalFlag';
import { KeptWorkNotice } from '@/components/KeptWorkNotice';
import { keptWorkKey, useKeptWork } from '@/lib/keptWork';

const SECTION_STYLE = {
  backgroundColor: 'var(--color-cream)',
  borderLeftColor: 'var(--color-sage-400)',
} as const;
const HEADING_STYLE = { color: 'var(--color-sage-800)' } as const;
const SUBTLE_STYLE = { color: 'var(--color-sage-300)' } as const;
const CHIP_STYLE = {
  backgroundColor: 'var(--color-paper)',
  borderColor: '#C9D6CF',
  color: 'var(--color-sage-800)',
} as const;

const SECTION_CLS = 'rounded-lg border-l-4 border-y border-r border-sage-800/[8%] p-4 space-y-3';
/** A save refused because the draft is no longer in draft status. */
const DRAFT_MOVED_ON = 'Someone changed this draft while you had it open. Reload the page to see it as it is now.';

const STATUS_LABEL: Record<'draft' | 'finalized' | 'archived', string> = {
  draft: 'Draft',
  finalized: 'Finalized',
  archived: 'Archived',
};

export function AiToolsDraftDetail(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const supportContact = useSupportContact();
  // The email helper's input ceiling (the Want changes? box), from the
  // tenant config; the factory value until the config has loaded.
  const tenantConfig = useTenantConfig(tenantSlug).data?.config;
  const emailMaxChars = readAiInputMaxChars(tenantConfig, 'email');

  const capEligible = canUseAiTools(whoami);
  const { draft, isLoading, isError, error } = useAiDraft(tenantSlug, id, {
    enabled: capEligible,
  });

  const [bodyDraft, setBodyDraft] = useState('');
  // The body's textarea, where a note pinned to its words selects them.
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [saved, setSaved] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  // Resync the editable body when the loaded draft's identity changes
  // (initial load or route switch), not on every background refetch — so
  // an in-progress edit is never clobbered.
  const syncedIdRef = useRef<string | null>(null);
  // An expired sign-in keeps the unsaved edit (src/lib/keptWork.ts). Put
  // back before the saved body loads, it wins the first sync below.
  const keptBodyRef = useRef<string | null>(null);
  const kept = useKeptWork<{ body: string }>(
    keptWorkKey(tenantSlug, 'draft', id),
    () => (draft === undefined || draft === null || bodyDraft === draft.output_text ? null : { body: bodyDraft }),
    (k) => {
      keptBodyRef.current = k.body;
      setBodyDraft(k.body);
    },
  );
  useEffect(() => {
    if (draft && draft.id !== syncedIdRef.current) {
      syncedIdRef.current = draft.id;
      setBodyDraft(keptBodyRef.current ?? draft.output_text);
      keptBodyRef.current = null;
    }
  }, [draft]);
  // Saved: the kept copy has done its job.
  useEffect(() => {
    if (saved) kept.clear();
  }, [saved]);

  const updateMutation = useMutation({
    mutationFn: (input: UpdateDraftInput): Promise<UpdateDraftResponse> =>
      aiToolsApi.updateDraft(tenantSlug, id, input),
    onSuccess: () => {
      setMutationError(null);
      void queryClient.invalidateQueries({ queryKey: ['ai-tools', 'drafts', tenantSlug] });
      void queryClient.invalidateQueries({ queryKey: ['ai-tools', 'draft', tenantSlug, id] });
    },
    onError: (e: unknown) => {
      setSaved(false);
      // Every 409 means the draft left draft status after this page loaded
      // it (an edit or a status change); the server's wording is not read.
      if (e instanceof ApiError && e.status === 409) {
        setMutationError(DRAFT_MOVED_ON);
      } else if (e instanceof ApiError && e.status === 403) {
        setMutationError(
          contactSentence("You can't change this draft. Ask the person who wrote it, or {contact_admin}.", supportContact),
        );
      } else if (e instanceof ApiError && e.status === 404) {
        setMutationError("This draft isn't here any more. Go back to your drafts and pick another.");
      } else {
        setMutationError(pageWriteText(e, supportContact));
      }
    },
  });

  // X.3.4.8: hard delete (Archive -> Delete refactor). On success, drop
  // back to the inbox and invalidate the list query so the deleted row
  // disappears. invalidateQueries (not setQueryData) per the differing
  // list-vs-detail shapes (feedback_invalidate_queries_vs_set_query_data).
  const deleteMutation = useMutation({
    mutationFn: (): Promise<void> => aiToolsApi.deleteDraft(tenantSlug, id),
    onSuccess: () => {
      setMutationError(null);
      void queryClient.invalidateQueries({ queryKey: ['ai-tools', 'drafts', tenantSlug] });
      navigate(`/t/${tenantSlug}/ai-tools/drafts`);
    },
    onError: (e: unknown) => {
      setSaved(false);
      if (e instanceof ApiError && e.status === 404) {
        setMutationError("This draft isn't here any more. Go back to your drafts and pick another.");
      } else if (e instanceof ApiError && e.status === 403) {
        setMutationError(
          contactSentence("You can't delete this draft. Ask the person who wrote it, or {contact_admin}.", supportContact),
        );
      } else {
        setMutationError(pageWriteText(e, supportContact));
      }
    },
  });


  // Second-round edits (email drafts): revise the CURRENT body (hand edits
  // included) per the operator's change request. The original form context
  // rides from input_payload.form_input; the panel only renders when that
  // context exists (see the email branch below). Client-side only until
  // the operator clicks Save changes, so the persisted row is never
  // clobbered by a refine they walk away from; the previous on-screen text
  // stays restorable meanwhile.
  const [previousBody, setPreviousBody] = useState<string | null>(null);
  // WS-9: the last refine failure, as a registry key (errorCopy.ts owns
  // the copy) or a refusal for length with the box it names; null = no
  // error showing.
  const [refineFailure, setRefineFailure] = useState<AiToolFailure | null>(null);
  const onCeilingRefusal = useInvalidateOnCeilingRefusal(tenantSlug);
  // The panel's sentence, built at render: a refusal for length says the
  // server's number, else the named box's ceiling as the config reads now.
  const refineErrorText = (f: AiToolFailure): string => {
    const copy = aiToolFailureCopy(f, (entry) => readAiInputMaxChars(tenantConfig, entry));
    return errorText('email_refine', copy.key, supportContact, copy.vars);
  };
  // The refine request and the body it replaces are built at the press
  // and travel as the variables.
  const refineMutation = useMutation({
    mutationFn: (v: { request: Parameters<typeof aiToolsApi.refineEmail>[1] | null; bodyBefore: string }) => {
      if (v.request === null) throw new Error('refine called without the original draft context');
      return aiToolsApi.refineEmail(tenantSlug, v.request);
    },
    onSuccess: (res, v) => {
      setPreviousBody(v.bodyBefore);
      setBodyDraft(res.email_body);
      setRefineFailure(null);
      setSaved(false);
    },
    onError: (e: unknown, v) => {
      onCeilingRefusal(e);
      // The refine carries the saved brief, which this page cannot change,
      // so a refusal of the recipient, topic or special instructions is the
      // carried case. A refusal naming no box is about Want changes?.
      setRefineFailure(aiToolFailureOf(e, 'email_refine', 'instruction', v.request, aiRefineKeyOf));
    },
  });

  const handleRefine = (instruction: string): void => {
    const fi = draft?.input_payload.form_input;
    if (!draft || !fi?.recipient || !fi?.topic) {
      // Refused inside the mutation, so it reads as a refine failure.
      refineMutation.mutate({ request: null, bodyBefore: bodyDraft });
      return;
    }
    refineMutation.mutate({
      request: {
        current_body: bodyDraft,
        instruction,
        recipient: fi.recipient,
        topic: fi.topic,
        ...(fi.constraints ? { constraints: fi.constraints } : {}),
        mode: draft.input_payload.mode === 'spice' ? 'spice' : 'care_management',
        ...(fi.tone ? { tone: fi.tone } : {}),
      },
      bodyBefore: bodyDraft,
    });
  };

  const handleRestorePrevious = (): void => {
    if (previousBody === null) return;
    setBodyDraft(previousBody);
    setPreviousBody(null);
    setSaved(false);
  };

  const is404 = isError && error instanceof ApiError && error.status === 404;

  const header = (
    <AiToolsHeader
      tenantSlug={tenantSlug}
      title="Draft"
      rightLink={{ to: `/t/${tenantSlug}/ai-tools/drafts`, label: '← All drafts' }}
    />
  );

  if (isLoading) {
    return (
      <PageShell maxWidth="xl" testId="ai-tools-draft-detail-main" access={{ allowed: capEligible, deniedContent: <EmptyAiToolsTile /> }}>
        {header}
        <p className="text-sm text-ink-soft" data-testid="draft-detail-loading">
          Loading draft…
        </p>
      </PageShell>
    );
  }

  if (is404 || !draft) {
    return (
      <PageShell maxWidth="xl" testId="ai-tools-draft-detail-main" access={{ allowed: capEligible, deniedContent: <EmptyAiToolsTile /> }}>
        {header}
        <p
          className="text-sm text-ink-soft bg-sand border border-sage-800/[8%] rounded-lg px-4 py-6 text-center"
          data-testid="draft-detail-not-available"
        >
          This draft isn't here any more. Go back to your drafts and pick another.
        </p>
      </PageShell>
    );
  }

  if (isError) {
    return (
      <PageShell maxWidth="xl" testId="ai-tools-draft-detail-main" access={{ allowed: capEligible, deniedContent: <EmptyAiToolsTile /> }}>
        {header}
        <p
          className="text-sm text-warm-700 bg-warm-50 border border-warm-200 rounded-lg px-3 py-2"
          role="alert"
          data-testid="draft-detail-error"
        >
          {contactSentence(
            "This draft didn't open. Reload the page, and if it happens again, tell {contact_system}.",
            supportContact,
          )}
        </p>
      </PageShell>
    );
  }

  // A medication list is edited in its own tool (the review grid, the
  // client link, the date), so its draft opens there with the id to resume.
  if (draft.tool_name === 'med_list') {
    return (
      <Navigate
        to={`/t/${tenantSlug}/ai-tools/med-list?draft=${encodeURIComponent(draft.id)}`}
        replace
      />
    );
  }
  // A report still in draft reopens in the builder, where it is edited and
  // saved back to the same row. The Assessment Letter has no builder face,
  // and a finalized or archived report is read here.
  if (
    draft.tool_name === 'assessment' &&
    draft.status === 'draft' &&
    draft.input_payload.mode !== 'letter'
  ) {
    return (
      <Navigate
        to={`/t/${tenantSlug}/ai-tools/assessment?draft=${encodeURIComponent(draft.id)}`}
        replace
      />
    );
  }

  const formInput = draft.input_payload.form_input ?? {};
  const annotations = draft.input_payload.annotations ?? [];
  const billing = draft.input_payload.billing_suggestion;
  // X.3.4.10: SPICE drafts hide billing and show the photo/video reminder.
  // Pre-SPICE drafts have no `mode` key → read as Care Management. (SPICE
  // drafts also persist no billing_suggestion, so the billing guard is
  // two-deep: absent value AND the mode gate.)
  const isSpice = draft.input_payload.mode === 'spice';
  // An Assessment Letter saved under the assessment tool.
  const isLetter = draft.input_payload.mode === 'letter';
  const isDraftStatus = draft.status === 'draft';
  const dirty = bodyDraft !== draft.output_text;
  const pending = updateMutation.isPending;

  const handleSave = (): void => {
    setSaved(false);
    updateMutation.mutate(
      { email_body: bodyDraft },
      { onSuccess: () => setSaved(true) },
    );
  };

  const handleDelete = (): void => {
    setSaved(false);
    deleteMutation.mutate();
  };

  // Finalized assessment reports and letters: read-only report text + a
  // "Download .docx" link. The email-shaped "About this email" /
  // annotations / billing / body-editor below are email-only. Download
  // auth rides the SWA same-origin cookie via the anchor href (no
  // fetch+blob), identical to AssessmentDrafter.
  if (draft.tool_name === 'assessment') {
    return (
      <PageShell maxWidth="xl" testId="ai-tools-draft-detail-main" access={{ allowed: capEligible, deniedContent: <EmptyAiToolsTile /> }}>
        {header}
        <section className={SECTION_CLS} style={SECTION_STYLE} data-testid="draft-detail-output">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold mr-1" style={HEADING_STYLE}>
              {isLetter ? 'Summary Assessment Letter' : isSpice ? 'SPICE Assessment Report' : 'Assessment report'}
            </h2>
            {isLetter && (
              <span
                className="text-xs font-medium px-2 py-0.5 rounded-full border"
                style={CHIP_STYLE}
                data-testid="draft-detail-mode-letter"
              >
                Letter
              </span>
            )}
            {/* X.3.5.4: SPICE chip so a reopened SPICE report reads as SPICE. */}
            {isSpice && (
              <span
                className="text-xs font-medium px-2 py-0.5 rounded-full border"
                style={CHIP_STYLE}
                data-testid="draft-detail-mode-spice"
              >
                SPICE
              </span>
            )}
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full border"
              style={CHIP_STYLE}
              data-testid="draft-detail-status"
            >
              {STATUS_LABEL[draft.status]}
            </span>
            <span className="text-xs" style={SUBTLE_STYLE}>
              {practiceDayOf(draft.created_at)}
            </span>
          </div>

          <pre
            className="w-full whitespace-pre-wrap font-sans text-sm text-ink bg-white border border-sage-800/[8%] rounded-lg p-4"
            data-testid="draft-detail-assessment-body"
          >
            {draft.output_text}
          </pre>

          {/* The report keeps its formatting in the downloaded .docx (the
              deliverable); edits are made there, not inline. */}
          <p className="text-xs" style={SUBTLE_STYLE} data-testid="edit-in-word-nudge">
            To make edits, download and finalize in Word.
          </p>

          <HelperActionRows
            primary={
              <>
                <DownloadDocxButton
                  url={aiToolsApi.assessmentDownloadUrl(tenantSlug, draft.id)}
                  filename={isLetter ? 'assessment-letter.docx' : 'assessment.docx'}
                  variant="primary"
                  testId="assessment-download-link"
                >
                  Download .docx
                </DownloadDocxButton>
                <DocumentPrintPdfButton
                  url={aiToolsApi.assessmentDownloadUrl(tenantSlug, draft.id)}
                  filename={isLetter ? 'assessment-letter.docx' : 'assessment.docx'}
                  testId="assessment-draft-print-pdf"
                />
                <SaveToClientDocumentsButton
                  tenantSlug={tenantSlug}
                  draftId={draft.id}
                  clientId={draft.client_id}
                  testId="assessment-draft-save-to-documents"
                />
              </>
            }
            secondary={
              <Button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                variant="secondary"
                data-testid="draft-detail-delete"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            }
          />

          {mutationError !== null && (
            <p
              className="text-sm text-warm-700 bg-warm-50 border border-warm-200 rounded-lg px-3 py-2"
              role="alert"
              data-testid="draft-detail-mutation-error"
            >
              {mutationError}
            </p>
          )}
        </section>
      </PageShell>
    );
  }

  // X.3.8: brand_formatter drafts render read-only (the formatted text) with
  // a "Download .docx" link, mirroring the assessment branch. The branded
  // document is produced server-side from the persisted structured blocks;
  // the reopen path offers download + delete, not inline editing.
  if (draft.tool_name === 'brand_formatter') {
    return (
      <PageShell maxWidth="xl" testId="ai-tools-draft-detail-main" access={{ allowed: capEligible, deniedContent: <EmptyAiToolsTile /> }}>
        {header}
        <section className={SECTION_CLS} style={SECTION_STYLE} data-testid="draft-detail-output">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold mr-1" style={HEADING_STYLE}>
              Formatted document
            </h2>
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full border"
              style={CHIP_STYLE}
              data-testid="draft-detail-status"
            >
              {STATUS_LABEL[draft.status]}
            </span>
            <span className="text-xs" style={SUBTLE_STYLE}>
              {practiceDayOf(draft.created_at)}
            </span>
          </div>

          <pre
            className="w-full whitespace-pre-wrap font-sans text-sm text-ink bg-white border border-sage-800/[8%] rounded-lg p-4"
            data-testid="draft-detail-brand-formatter-body"
          >
            {draft.output_text}
          </pre>

          {/* The branded document keeps its formatting in the downloaded .docx
              (the deliverable); edits are made there, not inline. */}
          <p className="text-xs" style={SUBTLE_STYLE} data-testid="edit-in-word-nudge">
            To make edits, download and finalize in Word.
          </p>

          <HelperActionRows
            primary={
              <DownloadDocxButton
                url={aiToolsApi.brandFormatDownloadUrl(tenantSlug, draft.id)}
                filename="brand-formatted.docx"
                variant="primary"
                testId="brand-formatter-download-link"
              >
                Download .docx
              </DownloadDocxButton>
            }
            secondary={
              <Button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                variant="secondary"
                data-testid="draft-detail-delete"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            }
          />

          {mutationError !== null && (
            <p
              className="text-sm text-warm-700 bg-warm-50 border border-warm-200 rounded-lg px-3 py-2"
              role="alert"
              data-testid="draft-detail-mutation-error"
            >
              {mutationError}
            </p>
          )}
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="xl" testId="ai-tools-draft-detail-main" access={{ allowed: capEligible, deniedContent: <EmptyAiToolsTile /> }}>
      {header}

      {/* The medical or care content this email carries, named above
          everything else, the same as when it was drafted. */}
      {isSpice && draft.input_payload.medical_flag && (
        <SpiceMedicalFlag text={draft.input_payload.medical_flag} />
      )}
      {/* X.3.4.10: SPICE drafts remind the SPICY to attach photos/videos;
          they often edit then send straight from this detail view. */}
      {isSpice && <SpicePhotoReminder />}

      {/* Saved input (read-only). */}
      <section className={SECTION_CLS} style={SECTION_STYLE} data-testid="draft-detail-input">
        <h2 className="text-lg font-semibold" style={HEADING_STYLE}>
          About this email
        </h2>
        <dl className="space-y-2 text-sm" style={{ color: 'var(--color-ink)' }}>
          <div>
            <dt className="text-xs font-medium" style={SUBTLE_STYLE}>
              Who is this for?
            </dt>
            <dd>{formInput.recipient ?? '(not recorded)'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium" style={SUBTLE_STYLE}>
              Topic
            </dt>
            <dd className="whitespace-pre-wrap">{formInput.topic ?? '(not recorded)'}</dd>
          </div>
        </dl>
      </section>

      {/* Editable draft + read-only annotations/billing. */}
      <section className={SECTION_CLS} style={SECTION_STYLE} data-testid="draft-detail-output">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-lg font-semibold mr-1" style={HEADING_STYLE}>
            Your draft
          </h2>
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-full border"
            style={CHIP_STYLE}
            data-testid="draft-detail-status"
          >
            {STATUS_LABEL[draft.status]}
          </span>
          <span className="text-xs" style={SUBTLE_STYLE}>
            {practiceDayOf(draft.created_at)}
          </span>
        </div>

        {!isDraftStatus && (
          <p className="text-xs" style={SUBTLE_STYLE} data-testid="draft-detail-readonly-note">
            This draft is {STATUS_LABEL[draft.status].toLowerCase()} and can no longer be edited.
          </p>
        )}

        {kept.restored && <KeptWorkNotice onDiscard={kept.discard} />}
        <textarea
          className="w-full min-h-64 whitespace-pre-wrap font-sans text-sm text-ink bg-white border border-sage-800/[8%] rounded-lg p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 disabled:bg-sand disabled:text-ink-soft"
          ref={bodyRef}
          value={bodyDraft}
          onChange={(e) => setBodyDraft(e.target.value)}
          readOnly={!isDraftStatus}
          disabled={!isDraftStatus}
          aria-label="Email draft body"
          data-testid="draft-detail-body"
        />

        <EmailActionRows
          body={bodyDraft}
          logAction={
            <LogAsCaseNoteAction
              body={bodyDraft}
              suggestedHours={isSpice ? undefined : billing?.hours}
              occurredAt={practiceDayOf(draft.created_at) ?? undefined}
            />
          }
          actions={
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSave}
                disabled={!isDraftStatus || !dirty || pending}
                data-testid="draft-detail-save"
              >
                {pending ? 'Saving…' : 'Save to drafts'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                disabled={pending || deleteMutation.isPending}
                data-testid="draft-detail-delete"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </>
          }
        />

        {saved && !dirty && (
          <p className="text-xs text-sage-700" role="status" data-testid="draft-detail-saved">
            ✓ Saved
          </p>
        )}
        {mutationError !== null && (
          <p
            className="text-sm text-warm-700 bg-warm-50 border border-warm-200 rounded-lg px-3 py-2"
            role="alert"
            data-testid="draft-detail-mutation-error"
          >
            {mutationError}
          </p>
        )}

        {/* A note shows while its words are in the body as it reads now. */}
        {(visibleAnnotations(annotations, bodyDraft).length > 0 || billing) && (
          <div className="space-y-2 pt-1">
            <p className="text-xs" style={SUBTLE_STYLE}>
              (from initial draft)
            </p>
            <EmailAnnotationsBlock
              annotations={annotations}
              body={bodyDraft}
              bodyRef={bodyRef}
              selectable={isDraftStatus}
            />
            {billing && !isSpice && <EmailBillingBlock suggestion={billing} />}
          </div>
        )}
      </section>

      {/* Second-round edits: say what to change and run it again. Needs the
          original form context (recipient + topic) from the saved
          input_payload; very old rows without it just don't get the panel.
          The refined text lands in the editor above; nothing is persisted
          until the operator clicks Save changes. */}
      {isDraftStatus && formInput.recipient && formInput.topic && (
        <>
          <RefinePanel
            onRefine={handleRefine}
            pending={refineMutation.isPending}
            error={refineFailure === null ? null : refineErrorText(refineFailure)}
            onRestore={previousBody !== null ? handleRestorePrevious : undefined}
            maxChars={emailMaxChars}
            testIdPrefix="draft-detail-refine"
          />
          {previousBody !== null && dirty && (
            <p className="text-xs" style={SUBTLE_STYLE} data-testid="draft-detail-refine-unsaved">
              The new version is in the editor above. Click Save changes to keep it.
            </p>
          )}
        </>
      )}
    </PageShell>
  );
}
